import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, Calendar, Facebook, Twitter, Linkedin, Link2, AlertTriangle, MessageSquareReply, Tag } from "lucide-react";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import NewsCard, { CategoryBadge } from "@/components/NewsCard";
import { CATEGORY_META, TYPE_LABELS, formatDate, formatTime } from "@/lib/constants";
import { toYoutubeEmbed } from "@/lib/media";
import { toast } from "sonner";

function Block({ block }) {
  if (block.type === "subtitle") return <h2>{block.text}</h2>;
  if (block.type === "quote")
    return <blockquote className="border-l-4 border-nx-orange pl-5 my-8 font-serif text-xl md:text-2xl italic text-nx-dark leading-snug">{block.text}</blockquote>;
  if (block.type === "image" && block.url)
    return (
      <figure className="my-8">
        <img src={block.url} alt={block.alt || ""} className="w-full rounded-md" loading="lazy" />
        {(block.caption || block.credit) && <figcaption className="mt-2 text-xs text-gray-500 italic">{block.caption}{block.credit ? ` · ${block.credit}` : ""}</figcaption>}
      </figure>
    );
  if (block.type === "video") {
    const embed = toYoutubeEmbed(block.url);
    if (!embed) return null;
    return <div className="my-8 aspect-video rounded-md overflow-hidden bg-black"><iframe className="w-full h-full" src={embed} title="video" allowFullScreen /></div>;
  }
  if (block.type === "demo")
    return <div className="my-8 bg-nx-soft border border-gray-200 rounded-md p-4 text-sm text-gray-500 flex gap-2"><Info /> <span>{block.text}</span></div>;
  return <p>{block.text}</p>;
}
function Info() { return <span className="text-[10px] font-bold uppercase tracking-widest bg-gray-200 text-gray-600 px-2 py-1 rounded self-start">Demo</span>; }

export default function ArticleDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null); setError(false);
    api.get(`/articles/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  const article = data?.article;
  const meta = article && CATEGORY_META[article.category];

  useSeo(article ? {
    title: article.title, description: article.subtitle, image: article.image, type: "article",
    jsonLd: {
      "@context": "https://schema.org", "@type": "NewsArticle", headline: article.title,
      description: article.subtitle, image: [article.image], datePublished: article.date,
      author: [{ "@type": "Person", name: article.author }],
      publisher: { "@type": "Organization", name: "NEXODUM" },
    },
  } : {});

  if (error) return (
    <div className="max-w-container mx-auto px-4 py-32 text-center">
      <h1 className="font-serif text-3xl font-black text-nx-dark">Nota no encontrada</h1>
      <Link to="/" className="mt-4 inline-block text-nx-orange font-bold">Volver al inicio</Link>
    </div>
  );
  if (!data) return <div className="max-w-3xl mx-auto px-4 py-20 animate-pulse space-y-4"><div className="h-8 bg-gray-100 rounded w-1/3" /><div className="h-16 bg-gray-100 rounded" /><div className="h-96 bg-gray-100 rounded" /></div>;

  const share = (net) => {
    const url = window.location.href;
    const maps = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(article.title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    if (net === "copy") { navigator.clipboard.writeText(url); toast.success("Enlace copiado"); return; }
    window.open(maps[net], "_blank", "noopener");
  };

  return (
    <div data-testid="article-detail">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-8">
        <nav className="text-xs text-gray-500 mb-5 flex items-center gap-2" aria-label="breadcrumb">
          <Link to="/" className="hover:text-nx-orange">Inicio</Link><span>/</span>
          <Link to={`/${article.category}`} className="hover:text-nx-orange capitalize">{meta?.label}</Link>
        </nav>

        <div className="flex items-center gap-2 mb-4">
          <CategoryBadge category={article.category} />
          <span className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{TYPE_LABELS[article.type]}</span>
          {article.breaking && <span className="text-[10px] font-bold tracking-widest uppercase bg-red-600 text-white px-2 py-1">Última hora</span>}
        </div>

        <h1 className="font-serif font-black text-3xl md:text-5xl leading-[1.08] text-nx-dark">{article.title}</h1>
        <p className="mt-4 text-lg md:text-xl text-gray-600 leading-relaxed font-light">{article.subtitle}</p>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-4 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {article.author_avatar && <img src={article.author_avatar} alt={article.author} className="w-11 h-11 rounded-full object-cover" />}
            <div className="text-sm">
              <p className="font-bold text-nx-dark">{article.author}</p>
              <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                <span className="flex items-center gap-1"><Calendar size={12} />{formatDate(article.date)}, {formatTime(article.date)}</span>
                <span className="flex items-center gap-1"><Clock size={12} />{article.reading_time} min de lectura</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="share-buttons">
            <button onClick={() => share("facebook")} aria-label="Compartir en Facebook" className="p-2 rounded-full border border-gray-200 text-gray-600 hover:bg-nx-dark hover:text-white transition-colors"><Facebook size={15} /></button>
            <button onClick={() => share("x")} aria-label="Compartir en X" className="p-2 rounded-full border border-gray-200 text-gray-600 hover:bg-nx-dark hover:text-white transition-colors"><Twitter size={15} /></button>
            <button onClick={() => share("linkedin")} aria-label="Compartir en LinkedIn" className="p-2 rounded-full border border-gray-200 text-gray-600 hover:bg-nx-dark hover:text-white transition-colors"><Linkedin size={15} /></button>
            <button onClick={() => share("copy")} data-testid="share-copy" aria-label="Copiar enlace" className="p-2 rounded-full border border-gray-200 text-gray-600 hover:bg-nx-dark hover:text-white transition-colors"><Link2 size={15} /></button>
          </div>
        </div>
      </article>

      {/* main image */}
      <figure className="max-w-4xl mx-auto px-4 sm:px-6 mb-8">
        {toYoutubeEmbed(article.video) ? (
          <div className="aspect-video rounded-md overflow-hidden bg-black">
            <iframe className="w-full h-full" src={toYoutubeEmbed(article.video)} title={article.title} allowFullScreen data-testid="article-video" />
          </div>
        ) : (
          <img src={article.image} alt={article.image_alt || article.title} className="w-full rounded-md" />
        )}
        {article.image_caption && <figcaption className="mt-2 text-xs text-gray-500 italic">{article.image_caption}{article.image_credit ? ` · ${article.image_credit}` : ""}</figcaption>}
      </figure>

      {/* body */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 nx-article-body" data-testid="article-body">
        {(article.content || []).map((b, i) => <Block key={i} block={b} />)}

        {/* gallery */}
        {article.gallery && article.gallery.length > 0 && (
          <div className="my-8" data-testid="article-gallery">
            <h3 className="font-bold text-nx-dark mb-3">Galería</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {article.gallery.map((g, i) => {
                const item = typeof g === "string" ? { url: g } : g;
                if (!item.url) return null;
                return (
                  <figure key={i}>
                    <img src={item.url} alt={item.alt || ""} className="w-full rounded-md aspect-video object-cover" loading="lazy" />
                    {(item.caption || item.credit) && <figcaption className="mt-1.5 text-xs text-gray-500 italic">{item.caption}{item.credit ? ` · ${item.credit}` : ""}</figcaption>}
                  </figure>
                );
              })}
            </div>
          </div>
        )}

        {/* sources */}
        {article.sources && article.sources.length > 0 && (
          <div className="my-8">
            <h3 className="font-bold text-nx-dark mb-2">Fuentes</h3>
            <ul className="list-disc pl-5 text-sm text-nx-blue space-y-1">
              {article.sources.map((s, i) => <li key={i}><a href={s} target="_blank" rel="noopener" className="hover:underline">{s}</a></li>)}
            </ul>
          </div>
        )}

        {/* tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap my-8 pt-6 border-t border-gray-200">
            <Tag size={15} className="text-gray-400" />
            {article.tags.map((t) => (
              <Link key={t} to={`/buscar?q=${encodeURIComponent(t)}`} className="text-xs bg-nx-soft border border-gray-200 rounded-full px-3 py-1 text-gray-600 hover:border-nx-orange hover:text-nx-orange transition-colors">#{t}</Link>
            ))}
          </div>
        )}

        {/* author box */}
        {article.author_bio && (
          <div className="my-8 bg-nx-soft rounded-md p-5 flex gap-4">
            {article.author_avatar && <img src={article.author_avatar} alt={article.author} className="w-14 h-14 rounded-full object-cover shrink-0" />}
            <div>
              <p className="font-bold text-nx-dark">{article.author}</p>
              <p className="text-xs text-nx-blue mb-1">{article.author_role}</p>
              <p className="text-sm text-gray-600">{article.author_bio}</p>
            </div>
          </div>
        )}

        {/* error / reply */}
        <div className="my-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-md p-5" data-testid="found-error-box">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle size={18} className="text-nx-orange" /><h3 className="font-bold text-nx-dark">¿Encontraste un error?</h3></div>
            <p className="text-sm text-gray-600 mb-3">En NEXODUM estamos comprometidos con la precisión. Podés comunicarte con nuestra redacción para solicitar una corrección.</p>
            <Link to="/contacto" className="text-sm font-bold text-nx-orange hover:underline">Reportar corrección →</Link>
          </div>
          <div className="border border-gray-200 rounded-md p-5" data-testid="reply-box">
            <div className="flex items-center gap-2 mb-2"><MessageSquareReply size={18} className="text-nx-blue" /><h3 className="font-bold text-nx-dark">Derecho a respuesta / réplica</h3></div>
            <p className="text-sm text-gray-600 mb-3">Si sos aludido en esta nota, tenés derecho a solicitar una respuesta o réplica.</p>
            <Link to="/contacto" className="text-sm font-bold text-nx-blue hover:underline">Ejercer derecho a réplica →</Link>
          </div>
        </div>
      </div>

      {/* related */}
      {data.related && data.related.length > 0 && (
        <section className="bg-nx-soft py-14 mt-10">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8"><span className="w-1.5 h-7 rounded-sm" style={{ backgroundColor: meta?.color }} /><h2 className="font-serif font-black text-2xl text-nx-dark">Noticias relacionadas</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {data.related.map((a) => <NewsCard key={a.id} article={a} variant="grid" />)}
            </div>
          </div>
        </section>
      )}

      {/* latest */}
      {data.latest && data.latest.length > 0 && (
        <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-center gap-3 mb-6"><span className="w-1.5 h-7 rounded-sm bg-nx-orange" /><h2 className="font-serif font-black text-2xl text-nx-dark">Últimas noticias</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            {data.latest.map((a) => <NewsCard key={a.id} article={a} variant="compact" />)}
          </div>
        </section>
      )}
    </div>
  );
}
